import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { RetailersService, UnregisteredUserRequest, UnregisteredUsersService } from '@src/app/shared/backend/clients';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-add-unregistered-user',
    templateUrl: './add-unregistered-user.component.html',
    styleUrl: './add-unregistered-user.component.scss',
    host: {
        class: 'app-add-unregistered-user'
    },
    standalone: false
})
export class AddUnregisteredUserComponent extends BaseViewComponent implements OnInit {

  combosData: { [key: string]: ComboData } = {
    retailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter)))
  };

  public unregisteredUserForm: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(100)]],
    lastname: ['', [Validators.required, Validators.maxLength(100)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator()]],
    romanisedLastName: ['', [latinCharsetRegexValidator()]],
    retailerIds: [[], [Validators.required]]
  });

  constructor(
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private fb: FormBuilder,
    private unregisteredUsersService: UnregisteredUsersService,
    private retailersService: RetailersService,
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    await this.initCombosData();
    this.changeDetector.detectChanges();
  }

  private async initCombosData() {
    await this.combosData['retailers'].getData(null, 1);
  }

  async onLazyLoadComboData(comboType: 'retailers', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'retailers'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  isSubmitDisabled(): boolean {
    return this.unregisteredUserForm.invalid;
  }

  async submitForm() {
    if (this.unregisteredUserForm.invalid) {
      return;
    }

    const retailerIds: number[] = (this.unregisteredUserForm.controls['retailerIds'].value || [])
      .map((id: any) => Number(id))
      .filter((id: number) => !Number.isNaN(id));

    const payload: UnregisteredUserRequest = {
      firstName: this.unregisteredUserForm.controls['firstname'].value,
      lastName: this.unregisteredUserForm.controls['lastname'].value,
      romanisedFirstName: this.unregisteredUserForm.controls['romanisedFirstName'].value,
      romanisedLastName: this.unregisteredUserForm.controls['romanisedLastName'].value,
      retailerIds
    };

    await firstValueFrom(this.unregisteredUsersService.apiUnregisteredUsersCreatePost(payload));

    this.unregisteredUserForm.reset();
    this.unregisteredUserForm.patchValue({
      retailerIds: []
    });

    this.router.navigate(['/unregistered-users']);
  }
}
